// settings
// https://swiperjs.com/swiper-api
/*
    <div
        data-controller="swiper"
        class="swiper" 
        data-carousel-options-value="{ }"
    >
        <div class="swiper-wrapper">
            <div class="swiper-slide">
                <div>Slide 1</div>
            </div>
            <div class="swiper-slide">
                <div>Slide 2</div>
            </div>
            <div class="swiper-slide">
                <div>Slide 3</div>
            </div>
        </div>

        <div class="swiper-button-next"></div>
        <div class="swiper-button-prev"></div>
    </div>
*/

import {
    Controller
} from "@hotwired/stimulus"
import Swiper from 'swiper';
import {
    Autoplay,
    Navigation,
    Pagination,
    EffectFade
} from 'swiper/modules'
//https://swiperjs.com/migration-guide-v10

export default class extends Controller {
    static values = {
        options: Object,
        type: String
    }

    connect() {
        if (this.element.swiper) {
            this.element.swiper.destroy(true, true)
        }

        if (this.typeValue === "kv") this.setupKvVariants()
        this.build()
    }

    build() {
        const typeOptions = {
            "brand-gallery": this.brandGalleryOptions,
            "kv": this.kvOptions
        }[this.typeValue] || this.optionsValue;
        const config = {
            modules: [Autoplay, Navigation, Pagination, EffectFade],
            ...this.defaultOptions,
            ...typeOptions
        };
        this.swiper = new Swiper(this.element, config);
    }

    disconnect() {
        if (this.kvMedia) {
            this.kvMedia.removeEventListener("change", this.onKvMediaChange)
            this.kvMedia = null
        }
        if (this.swiper) {
            this.swiper.destroy(true, true)
            this.swiper = null
        }
    }

    // 首頁 KV：直式圖只給手機、橫式圖只給桌機。
    // 依視窗寬度把不該出現的 slide 從 DOM 拿掉（保留節點），跨越斷點時重建。
    // 影片 slide 不動，避免 iframe 重新載入。
    setupKvVariants() {
        this.kvAllSlides = Array.from(this.element.querySelectorAll(".swiper-slide"))
        this.kvMedia = window.matchMedia("(max-width: 719.98px)")
        this.onKvMediaChange = () => {
            const wasRunning = this.swiper && this.swiper.autoplay.running
            this.swiper.destroy(true, true)
            this.applyKvVariant()
            this.build()
            if (!wasRunning) this.swiper.autoplay.stop()
        }
        this.kvMedia.addEventListener("change", this.onKvMediaChange)
        this.applyKvVariant()
    }

    applyKvVariant() {
        const wanted = this.kvMedia.matches ? "mobile" : "desktop"
        const wrapper = this.element.querySelector(".swiper-wrapper")
        const shouldShow = (slide) => {
            const variant = slide.dataset.kvVariant
            return !variant || variant === wanted
        }

        this.kvAllSlides.forEach((slide) => {
            if (!shouldShow(slide) && slide.parentNode === wrapper) slide.remove()
        })
        this.kvAllSlides.forEach((slide, index) => {
            if (!shouldShow(slide) || slide.parentNode === wrapper) return
            const next = this.kvAllSlides.slice(index + 1).find(s => s.parentNode === wrapper)
            wrapper.insertBefore(slide, next || null)
        })
    }

    get brandGalleryOptions() {
        return {
            slidesPerView: 1,
            spaceBetween: 0,
            slidesPerGroup: 1,
            pagination: false,
            breakpoints: {
                768: {
                    slidesPerView: 3
                }
            }
        }
    }

    // 首頁 KV：影片 + 主視覺輪播（fade），每張 slide 用 data-swiper-autoplay 指定停留秒數
    get kvOptions() {
        return {
            effect: "fade",
            fadeEffect: { crossFade: true },
            slidesPerView: 1,
            spaceBetween: 0,
            loop: false,
            rewind: true, // 不用 loop：loop 會搬動 slide 節點，影片 iframe 會被重新載入
            speed: 1000,
            navigation: {
                prevEl: this.element.querySelector(".swiper-button-prev"),
                nextEl: this.element.querySelector(".swiper-button-next")
            },
            pagination: {
                el: this.element.querySelector(".swiper-pagination"),
                clickable: true
            },
            autoplay: {
                delay: 6000,
                disableOnInteraction: false
            },
            on: {
                init: (swiper) => {
                    // 邀請卡蓋住首頁時先不要跑輪播，等 kv#skip 打開後再啟動
                    if (this.element.closest("#kv-video.fade")) swiper.autoplay.stop()
                    this.syncKvVideo(swiper)
                },
                slideChange: (swiper) => this.syncKvVideo(swiper)
            }
        }
    }

    // 切到影片 slide 時從頭播放，離開時暫停（YouTube IFrame API postMessage）
    syncKvVideo(swiper) {
        const iframe = this.element.querySelector("iframe")
        if (!iframe || !iframe.contentWindow) return

        const activeSlide = swiper.slides[swiper.activeIndex]
        const isVideoSlide = activeSlide && activeSlide.contains(iframe)
        const send = (func, args = []) => {
            iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func, args }), "*")
        }

        if (isVideoSlide) {
            send("seekTo", [0, true])
            send("playVideo")
        } else {
            send("pauseVideo")
        }
    }

    get defaultOptions() {
        return {
            slidesPerView: 1,
            spaceBetween: 0,
            speed: 1000,
            loop: true,
            pagination: true,
            navigation: {
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
            }
        }
    }
}